import { cloneElement, PropsWithChildren } from 'react';

import { Button, CheckOutline, forwardRef, rem, StyleProps, Text } from '@bonusly/design-system';

import { useActionInput } from '../../context';
import { useTagState } from './hooks/use-tag-state';
import { Tag } from './types';

export interface TagButtonProps extends PropsWithChildren, StyleProps {
	tag: Tag;
}

export const TagButton = forwardRef<TagButtonProps, 'button'>(({ tag, ...rest }: TagButtonProps, ref) => {
	const { editor } = useActionInput();
	const { isActive, isEnabled, schema } = useTagState(tag);
	const { startDraft } = useActionInput();

	if (!isEnabled) {
		return null;
	}

	const handleClick = () => {
		if (editor) {
			startDraft();
			editor.chain().insertContent(schema.trigger).focus().run();
		}
	};

	// Dynamic colors based on active state - icon uses currentColor to change with hover
	const bgColor = isActive ? schema.activeBg : 'transparent';
	const textColor = isActive ? schema.activeText : 'text.default';
	const borderColor = isActive ? 'transparent' : 'border.default';

	return (
		<Button
			variant="ghost"
			colorScheme="secondary"
			size="sm"
			h={rem(24)}
			minW="auto"
			px={rem(6)}
			py={rem(2)}
			gap={rem(4)}
			borderRadius={rem(8)}
			bg={bgColor}
			color={textColor}
			borderWidth="1px"
			borderColor={borderColor}
			_hover={{ bg: schema.activeBg, color: schema.activeText, borderColor: 'transparent' }}
			onClick={handleClick}
			ref={ref}
			{...rest}
		>
			{isActive ? <CheckOutline boxSize={4} color="currentColor" /> : cloneElement(schema.icon, { boxSize: 4, color: 'currentColor' })}
			<Text as="span" fontSize={rem(14)} lineHeight={1} whiteSpace="nowrap" textTransform="lowercase">
				{schema.label}
			</Text>
		</Button>
	);
});

TagButton.displayName = 'TagButton';
